import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  IconButton,
  Button,
  useColorModeValue,
  Skeleton,
  Avatar,
  Select,
} from '@chakra-ui/react';
import {
  EditIcon,
  DeleteIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';

const DevelopmentTable = ({
  tableData,
  handleEditUser,
  handleDeleteUser,
  handlePageChange,
  handleRowClick,
  page,
  totalPages,
  totalResult,
  rowsPerPage,
  handleRowsPerPageChange,
  loading,
  formatPhoneNumber,
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('gray.100', 'gray.700');
  const role = localStorage.getItem('role');
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const selectBg = useColorModeValue('white', 'gray.700');

  const roleLabels = {
    admin: 'Admin',
    companyUser: 'Company Admin',
    user: 'User',
    IRM: 'IRM',
    FSE: 'FSE',
  };

  const getRoleLabel = (userRole) => roleLabels[userRole] || userRole || 'N/A';

  const canEditUser = (userRole) =>
    role === 'companyUser' && ['user', 'companyUser'].includes(userRole);

  const canDeleteUser = (userRole) =>
    role === 'companyUser' && userRole === 'user';

  const renderTableRows = () => {
    if (loading) {
      return Array.from({ length: 5 }, (_, index) => (
        <Tr key={index}>
          {Array.from({ length: 6 }, (_, colIndex) => (
            <Td key={colIndex}>
              <Skeleton height="20px" />
            </Td>
          ))}
        </Tr>
      ));
    }

    if (!tableData || tableData.length === 0) {
      return (
        <Tr>
          <Td colSpan="6" textAlign="center">
            <Text fontSize="lg" fontWeight="medium" color="gray.500">
              No users found
            </Text>
          </Td>
        </Tr>
      );
    }

    return tableData.map((user, index) => (
      <Tr
        key={user._id || user.id || `${user.email}-${index}`}
        onClick={() => handleRowClick(user)}
        cursor="pointer"
        _hover={{ bg: rowHoverBg }}
      >
        <Td>{index + 1 + (page - 1) * rowsPerPage}</Td>
        <Td>
          <Flex align="center" gap={3} minW={0}>
            <Avatar
              size="sm"
              name={user.name || user.username || user.email}
              src={user.profilePic || user.avatar || user.image}
            />
            <Text color={textColor} fontWeight="medium" isTruncated>
              {user.name || 'N/A'}
            </Text>
          </Flex>
        </Td>
        <Td wordBreak="break-word">{user.email || 'N/A'}</Td>
        <Td>{formatPhoneNumber(user.phoneNo)}</Td>
        <Td>{getRoleLabel(user.role)}</Td>
        <Td>
          <Flex gap="2">
            <IconButton
              isDisabled={!canEditUser(user.role)}
              aria-label="Edit"
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              onClick={(e) => {
                e.stopPropagation();
                handleEditUser(user);
              }}
            />
            <IconButton
              aria-label="Delete"
              isDisabled={!canDeleteUser(user.role)}
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteUser(user._id);
              }}
            />
          </Flex>
        </Td>
      </Tr>
    ));
  };

  const renderPagination = () => {
    const maxPagesToShow = 5;
    const pages = [];
    const isEllipsisShown = totalPages > maxPagesToShow;

    const createPageButton = (pageNumber) => (
      <Button
        key={pageNumber}
        onClick={() => handlePageChange(pageNumber)}
        colorScheme={page === pageNumber ? 'blue' : 'gray'}
        variant={page === pageNumber ? 'solid' : 'outline'}
        size="sm"
        minW="32px"
        px={0}
      >
        {pageNumber}
      </Button>
    );

    if (isEllipsisShown) {
      if (page > Math.ceil(maxPagesToShow / 2)) {
        pages.push(createPageButton(1));
        pages.push(
          <Text key="ellipsis-start" mx="1" fontSize="lg">
            ...
          </Text>,
        );
      }

      const startPage = Math.max(page - Math.floor(maxPagesToShow / 2), 1);
      const endPage = Math.min(
        page + Math.floor(maxPagesToShow / 2),
        totalPages,
      );
      for (let i = startPage; i <= endPage; i++) {
        pages.push(createPageButton(i));
      }

      if (page < totalPages - Math.floor(maxPagesToShow / 2)) {
        pages.push(
          <Text key="ellipsis-end" mx="1" fontSize="lg">
            ...
          </Text>,
        );
        pages.push(createPageButton(totalPages));
      }
    } else {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(createPageButton(i));
      }
    }

    return (
      <Flex justify="center" align="center" flexWrap="wrap" gap={1}>
        <IconButton
          aria-label="Previous Page"
          icon={<ChevronLeftIcon />}
          isDisabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
          size="sm"
        />
        {pages}
        <IconButton
          aria-label="Next Page"
          icon={<ChevronRightIcon />}
          isDisabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
          size="sm"
        />
      </Flex>
    );
  };

  const renderFooter = () => {
    const totalItems = totalResult || 0;
    const startItem = totalItems === 0 ? 0 : (page - 1) * rowsPerPage + 1;
    const endItem = Math.min(page * rowsPerPage, totalItems);
    const rowsPerPageOptions = [5, 9, 10, 20, 50];

    return (
      <Flex
        px="25px"
        pt={2}
        pb={5}
        align="center"
        justify="space-between"
        gap={4}
        flexWrap="wrap"
      >
        <Text color={mutedTextColor} fontSize="sm" minW="130px">
          {startItem}-{String(endItem).padStart(2, '0')} of {totalItems} Items
        </Text>

        {renderPagination()}

        <Flex align="center" gap={2} minW="150px" justify="flex-end">
          <Select
            size="sm"
            value={rowsPerPage}
            onChange={(e) => handleRowsPerPageChange(e.target.value)}
            w="72px"
            bg={selectBg}
            borderColor={borderColor}
            borderRadius="md"
          >
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {String(option).padStart(2, '0')}
              </option>
            ))}
          </Select>
          <Text color={mutedTextColor} fontSize="sm" whiteSpace="nowrap">
            items per page
          </Text>
        </Flex>
      </Flex>
    );
  };

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX="auto">
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text color={textColor} fontSize="22px" fontWeight="bold">
          Users
        </Text>
      </Flex>
      <Box overflowX="auto" maxHeight="calc(100vh - 100px)">
        <Table variant="striped" colorScheme="gray" tableLayout="fixed">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th borderColor={borderColor} w="72px">
                Sr
              </Th>
              <Th borderColor={borderColor} w="28%">
                User Name
              </Th>
              <Th borderColor={borderColor}>Email</Th>
              <Th borderColor={borderColor} w="150px">
                Phone No.
              </Th>
              <Th borderColor={borderColor} w="130px">
                Role
              </Th>
              <Th borderColor={borderColor} w="120px">
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>{renderTableRows()}</Tbody>
        </Table>
      </Box>
      {renderFooter()}
    </Card>
  );
};

DevelopmentTable.propTypes = {
  tableData: PropTypes.array.isRequired,
  handleEditUser: PropTypes.func.isRequired,
  handleDeleteUser: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalResult: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  handleRowsPerPageChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  formatPhoneNumber: PropTypes.func.isRequired,
};

export default DevelopmentTable;
